import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'

type Status = 'validating' | 'ready' | 'submitting' | 'success' | 'already' | 'invalid' | 'error'

function UnsubscribePage() {
  const [status, setStatus] = useState<Status>('validating')
  const [token, setToken] = useState<string | null>(null)

  useEffect(() => {
    const t = new URLSearchParams(window.location.search).get('token')
    if (!t) {
      setStatus('invalid')
      return
    }
    setToken(t)
    fetch(`/email/unsubscribe?token=${encodeURIComponent(t)}`)
      .then(async (r) => {
        const body = await r.json().catch(() => ({}))
        if (!r.ok) {
          setStatus('invalid')
          return
        }
        if (body?.reason === 'already_unsubscribed') {
          setStatus('already')
          return
        }
        if (body?.valid) {
          setStatus('ready')
          return
        }
        setStatus('invalid')
      })
      .catch(() => setStatus('error'))
  }, [])

  async function confirm() {
    if (!token) return
    setStatus('submitting')
    try {
      const res = await fetch('/email/unsubscribe', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ token }),
      })
      const body = await res.json().catch(() => ({}))
      if (!res.ok) {
        setStatus('error')
        return
      }
      if (body?.reason === 'already_unsubscribed') {
        setStatus('already')
        return
      }
      if (body?.success) {
        setStatus('success')
        return
      }
      setStatus('error')
    } catch {
      setStatus('error')
    }
  }

  return (
    <div className="min-h-dvh flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-md text-center">
        <h1 className="text-3xl uppercase tracking-tight mb-4">Email preferences</h1>
        {status === 'validating' && (
          <p className="text-foreground/70">Checking your link…</p>
        )}
        {status === 'ready' && (
          <>
            <p className="text-foreground/80 mb-8">
              Click below to unsubscribe from Tables and Bridges emails. You
              can always reach us at{' '}
              <a className="underline" href="mailto:info@tablesandbridges.com">
                info@tablesandbridges.com
              </a>
              .
            </p>
            <button
              onClick={confirm}
              className="inline-flex items-center justify-center rounded bg-primary text-primary-foreground px-6 py-3 font-semibold uppercase tracking-wider text-sm hover:opacity-90 transition"
            >
              Confirm unsubscribe
            </button>
          </>
        )}
        {status === 'submitting' && (
          <p className="text-foreground/70">Updating your preferences…</p>
        )}
        {status === 'success' && (
          <p className="text-foreground/80">
            You're unsubscribed. We won't send you any more emails.
          </p>
        )}
        {status === 'already' && (
          <p className="text-foreground/80">
            This email is already unsubscribed. No further action needed.
          </p>
        )}
        {status === 'invalid' && (
          <p className="text-foreground/80">
            This unsubscribe link is invalid or has expired. Reply to any email
            from us and we'll take care of it.
          </p>
        )}
        {status === 'error' && (
          <p className="text-foreground/80">
            Something went wrong. Please try again, or email{' '}
            <a className="underline" href="mailto:info@tablesandbridges.com">
              info@tablesandbridges.com
            </a>
            .
          </p>
        )}
      </div>
    </div>
  )
}

export const Route = createFileRoute('/unsubscribe')({
  component: UnsubscribePage,
  head: () => ({
    meta: [
      { title: 'Unsubscribe · Tables and Bridges' },
      { name: 'robots', content: 'noindex' },
    ],
  }),
})
